/* ─────────────────────────────────────────────────────────
 *  TableOps – Sound Service (singleton)
 *
 *  Owns a single lazily-created AudioContext + DawSynthEngine +
 *  PlaybackScheduler, reusing the MIDI-DAW playback stack rather
 *  than re-implementing audio. One instance for the whole app:
 *  short, non-overlapping UI cues never need more than one voice
 *  pipeline.
 *
 *  Single responsibility: turn a `DawMidiFile` cue into sound.
 *  - Lazy init (no AudioContext until the first cue actually fires).
 *  - Autoplay-policy resilient (resumes a suspended context; the
 *    browser only honours this after a user gesture — errors are
 *    swallowed so a pre-gesture cue degrades silently instead of
 *    throwing).
 *  - Mute-aware and idempotently disposable.
 * ───────────────────────────────────────────────────────── */
import type { DawMidiFile, DawSynthPatch } from '../../midi-daw/types/midi-daw-types'
import { DEFAULT_PATCHES } from '../../midi-daw/types/midi-daw-types'
import { DawSynthEngine } from '../../midi-daw/services/synth-engine'
import { PlaybackScheduler } from '../../midi-daw/services/playback-scheduler'

type AudioContextCtor = typeof AudioContext

class TableOpsSoundService {
  private ctx: AudioContext | null = null
  private synth: DawSynthEngine | null = null
  private sched: PlaybackScheduler | null = null
  private _muted = false
  private _masterVolume = 0.7
  private initFailed = false

  get muted(): boolean {
    return this._muted
  }

  setMuted(value: boolean): void {
    this._muted = value
    if (value) this.sched?.stop()
  }

  setMasterVolume(value: number): void {
    const clamped = Math.max(0, Math.min(1, value))
    this._masterVolume = clamped
    this.synth?.setMasterVolume(clamped)
  }

  /** Lazily build the audio graph. Returns false if audio is unavailable. */
  private ensure(): boolean {
    if (this.sched) return true
    if (this.initFailed) return false
    try {
      const Ctor: AudioContextCtor | undefined =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext
      if (!Ctor) {
        this.initFailed = true
        return false
      }
      const ctx = new Ctor()
      const synth = new DawSynthEngine(ctx)
      const sched = new PlaybackScheduler(ctx, synth, {
        onPositionUpdate: () => {},
        onPlaybackEnd: () => {},
      })
      synth.setMasterVolume(this._masterVolume)
      this.ctx = ctx
      this.synth = synth
      this.sched = sched
      return true
    } catch {
      this.initFailed = true
      return false
    }
  }

  /**
   * Play a one-shot cue from the start. Any in-flight cue is cut off
   * first — UI cues are short and must never pile up into a drone.
   */
  play(midi: DawMidiFile, patch: DawSynthPatch = DEFAULT_PATCHES.sine): void {
    if (this._muted) return
    if (!this.ensure() || !this.sched || !this.ctx) return
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch(() => {})
    }
    this.sched.stop()
    this.sched.load(midi)
    midi.tracks.forEach((_, i) => this.sched!.setPatch(i, patch))
    this.sched.play(0)
  }

  dispose(): void {
    this.sched?.dispose()
    this.ctx?.close().catch(() => {})
    this.ctx = null
    this.synth = null
    this.sched = null
  }
}

/** Process-wide singleton — one audio pipeline shared by all TableOps cues. */
export const tableOpsSoundService = new TableOpsSoundService()
